import { api } from '@xtnd/web/shared/api'
import type {
  EnhancedTenantConfig,
  UpdateTenantConfigDto,
  HealthCheckResponse,
  ClioFormData,
  QboFormData,
  EmailFormData
} from '@xtnd/web/shared/types/tenantSettingTypes';

class TenantSettingsApi {
  private readonly baseSource = '/tenants';

  /**
   * Get current user's tenant configuration
   */
  async getCurrentTenantConfig(): Promise<EnhancedTenantConfig> {
    const response = await api.get<EnhancedTenantConfig>(`${this.baseSource}/current/config`);
    return response;
  }

  /**
   * Get specific tenant configuration
   */
  async getTenantConfig(tenantId: string): Promise<EnhancedTenantConfig> {
    const response = await api.get<EnhancedTenantConfig>(`${this.baseSource}/${tenantId}/config`);
    return response;
  }

  /**
   * Update tenant configuration
   */
  async updateTenantConfig(
    tenantId: string,
    updates: UpdateTenantConfigDto
  ): Promise<EnhancedTenantConfig> {
    const response = await api.patch<EnhancedTenantConfig>(`${this.baseSource}/${tenantId}/config`, updates);
    return response;
  }

  /**
   * Update current user's tenant configuration
   */
  async updateCurrentTenantConfig(updates: UpdateTenantConfigDto): Promise<EnhancedTenantConfig> {
    const response = await api.patch<EnhancedTenantConfig>(`${this.baseSource}/current/config`, updates);
    return response;
  }

  /**
   * Get current tenant email templates configuration
   */
  async getCurrentEmailConfig(): Promise<any> {
    const response = await api.get<any>(`${this.baseSource}/current/config/email`);
    return response;
  }

  /**
   * Get current tenant PDF templates configuration
   */
  async getCurrentPdfConfig(): Promise<any> {
    const response = await api.get<any>(`${this.baseSource}/current/config/pdfGeneration`);
    return response;
  }

  /**
   * Update current tenant email templates configuration
   */
  async updateCurrentEmailConfig(config: any): Promise<any> {
    // Email configuration should be sent as part of tenant config modules
    const updates = {
      modules: {
        emailServiceConfig: config
      }
    };
    const response = await api.patch<any>(`${this.baseSource}/current/config`, updates);
    return response;
  }

  async sendEmailVerification(email: string): Promise<any> {
    const response = await api.post<any>(`${this.baseSource}/current/config/email/verify`, { email });
    return response;
  }

  async checkEmailVerificationStatus(email: string): Promise<any> {
    const response = await api.get<any>(`${this.baseSource}/current/config/email/verify/status/${encodeURIComponent(email)}`);
    return response;
  }

  async confirmEmailVerification(email: string, token?: string): Promise<any> {
    const response = await api.post<any>(`${this.baseSource}/current/config/email/verify/confirm`, { email, token });
    return response;
  }

  /**
   * Update current tenant PDF templates configuration
   */
  async updateCurrentPdfConfig(config: any): Promise<any> {
    const response = await api.post<any>(`/pdf-generation/current/config`, config);
    return response;
  }

  /**
   * Save Clio configuration
   */
  async saveClioConfig(tenantId: string, clioData: ClioFormData): Promise<EnhancedTenantConfig> {
    const webhookEndpoint = import.meta.env.VITE_API_URL || 'https://api.d.xtnd.legal'; // Use environment-specific API endpoint for webhooks

    const moduleUpdate = {
      modules: {
        clioAPI: {
          clientId: clioData.clientId,
          clientSecret: { ssmPath: `/tenants/${tenantId}/clioAPI/clientSecret` },
          authorizationCode: { ssmPath: `/tenants/${tenantId}/clioAPI/authorizationCode` },
          baseUrl: 'https://app.clio.com', // Use production by default
          redirectUri: `${webhookEndpoint}/auth/clio/callback`,
          clioWebHookUri: webhookEndpoint,
          webhooksConfigured: clioData.enableWebhooks,
          webhookSubscriptions: clioData.webhookSubscriptions,
          webhookUrl: `${webhookEndpoint}/webhook/clio/v1/${tenantId}`,
          settingsComponent: 'ClioConfigModal',
          healthCheckEndpoint: '/health/clioAPI',
        },
      },
    };

    // Update the configured status of the feature
    const featuresUpdate = {
      features: [
        {
          displayName: 'Clio API Integration',
          enabled: true,
          enabledOn: new Date().toISOString(),
          moduleName: 'clioAPI',
          configured: Boolean(clioData.clientId && clioData.clientSecret),
        },
      ],
    };

    return this.updateTenantConfig(tenantId, { ...moduleUpdate, ...featuresUpdate });
  }

  /**
   * Save Email configuration
   */
  async saveEmailConfig(tenantId: string, emailData: EmailFormData): Promise<EnhancedTenantConfig> {
    const moduleUpdate = {
      modules: {
        emailNotifications: {
          operationsSupportEmail: emailData.operationsSupportEmail,
          technicalSupportEmail: emailData.technicalSupportEmail,
          firmInvoiceFromEmail: emailData.firmInvoiceFromEmail,
          firmInvoiceBccEmail: emailData.firmInvoiceBccEmail,
          settingsComponent: 'EmailConfigModal',
          healthCheckEndpoint: '/health/emailNotifications',
        },
      },
    };

    // Update the configured status of the feature
    const featuresUpdate = {
      features: [
        {
          displayName: 'Email Notifications',
          enabled: true,
          enabledOn: new Date().toISOString(),
          moduleName: 'emailNotifications',
          configured: Boolean(emailData.operationsSupportEmail && emailData.technicalSupportEmail),
        },
      ],
    };

    return this.updateTenantConfig(tenantId, { ...moduleUpdate, ...featuresUpdate });
  }

  /**
   * Check health of specific module
   */
  async checkModuleHealth(moduleName: string): Promise<HealthCheckResponse> {
    const response = await api.get<HealthCheckResponse>(`/health/${moduleName}`);
    return response;
  }

  /**
   * Check health of all modules
   */
  async checkAllModulesHealth(): Promise<HealthCheckResponse> {
    const response = await api.get<HealthCheckResponse>('/health/modules');
    return response;
  }

  /**
   * Toggle feature flag
   */
  async toggleFeature(
    tenantId: string,
    moduleName: string,
    enabled: boolean
  ): Promise<EnhancedTenantConfig> {
    // First get current config to preserve other features
    const currentConfig = await this.getTenantConfig(tenantId);

    const updatedFeatures = currentConfig.features.map(feature => {
      if (feature.moduleName === moduleName) {
        return {
          ...feature,
          enabled,
          enabledOn: enabled ? new Date().toISOString() : feature.enabledOn,
        };
      }
      return feature;
    });

    return this.updateTenantConfig(tenantId, { features: updatedFeatures });
  }

  /**
   * Configure webhooks for Clio integration
   */
  async configureWebhooks(tenantId: string, webhookSubscriptions: import('@xtnd/web/shared/types/tenant-config.types').ClioWebhookSubscription[]): Promise<{ success: boolean; webhooks: any[] }> {
    const response = await api.post<{ success: boolean; webhooks: any[] }>(`/webhook/clio/${tenantId}/configure`, {
      subscriptions: webhookSubscriptions
    });
    return response;
  }

  /**
   * Configure a single Clio webhook
   */
  async configureClioWebhook(config: {
    modelName: string;
    events: string;
    webhookId?: string;
  }): Promise<void> {
    await api.post<void>(`/tenants/current/webhook/clio/configure`, config);
  }

  /**
   * Get resolved Clio configuration for current tenant (with SSM references resolved)
   */
  async getCurrentTenantClioConfig(): Promise<ClioFormData & { baseUrl: string; redirectUri: string }> {
    const response = await api.get<ClioFormData & { baseUrl: string; redirectUri: string }>(`${this.baseSource}/current/clio/config`);
    return response;
  }

  /**
   * Test API connection and get Clio integration status
   */
  async testConnection(): Promise<{
    connected: boolean;
    apiStatus: string;
    clioConfigured: boolean;
    error?: string
  }> {
    try {
      // Test basic API connection
      const healthResponse = await api.get<{ status: string }>('/config/health');
      const apiConnected = healthResponse.status === 'healthy';

      if (!apiConnected) {
        return {
          connected: false,
          apiStatus: 'disconnected',
          clioConfigured: false,
          error: 'API not responding'
        };
      }

      // Test Clio configuration
      try {
        const configResponse = await this.getCurrentTenantConfig();
        const clioModule = configResponse.modules?.clioAPI;

        // Check for required Clio credentials (can be string values or SSM path references)
        const hasClientId = !!(
          clioModule?.clientId &&
          (typeof clioModule.clientId === 'string' || clioModule.clientId?.ssmPath)
        );

        const hasClientSecret = !!(
          clioModule?.clientSecret &&
          (typeof clioModule.clientSecret === 'string' || clioModule.clientSecret?.ssmPath)
        );

        const hasAuthCode = !!(
          clioModule?.authorizationCode &&
          (typeof clioModule.authorizationCode === 'string' || clioModule.authorizationCode?.ssmPath)
        );

        // Clio is configured if we have clientId, clientSecret, and authorizationCode
        const clioConfigured = hasClientId && hasClientSecret && hasAuthCode;

        return {
          connected: true,
          apiStatus: 'connected',
          clioConfigured,
        };
      } catch {
        return {
          connected: true,
          apiStatus: 'connected',
          clioConfigured: false,
          error: 'Clio not configured'
        };
      }
    } catch (error) {
      return {
        connected: false,
        apiStatus: 'error',
        clioConfigured: false,
        error: error instanceof Error ? error.message : 'Unknown connection error'
      };
    }
  }

  /**
   * Save QBO configuration for current tenant
   */
  async saveQboConfiguration(data: QboFormData): Promise<EnhancedTenantConfig> {
    const webhookEndpoint = import.meta.env.VITE_API_URL || 'https://api.d.xtnd.legal';

    const qboModule = {
      clientId: data.clientId,
      realmId: data.realmId,
      baseUrl: 'https://sandbox-quickbooks.api.intuit.com',
      redirectUri: `${import.meta.env.VITE_API_URL || 'http://localhost:8080'}/auth/qbo/callback`,
      qboWebhookUri: webhookEndpoint,
      webhooksConfigured: data.enableWebhooks,
      webhookSubscriptions: data.webhookSubscriptions,
      healthCheckEndpoint: '/health/qboAPI',
      settingsComponent: 'QBOConfigModal',
      webhookUrl: `${webhookEndpoint}/webhook/qbo/v1/0000000001`,
      // Include secrets if provided
      ...(data.appId && { appId: data.appId }),
      ...(data.clientSecret && { clientSecret: data.clientSecret }),
      ...(data.accessToken && { accessToken: data.accessToken }),
      ...(data.refreshToken && { refreshToken: data.refreshToken }),
    };

    return this.updateCurrentTenantConfig({
      modules: {
        qboAPI: qboModule,
      },
    });
  }

  /**
   * Test QBO connection status
   */
  async testQboConnection(): Promise<{
    connected: boolean;
    apiStatus: string;
    qboConfigured: boolean;
    error?: string
  }> {
    try {
      // Test basic API connection
      const healthResponse = await api.get<{ status: number }>('/health');
      const apiConnected = healthResponse.status === 200;

      if (!apiConnected) {
        return {
          connected: false,
          apiStatus: 'disconnected',
          qboConfigured: false,
          error: 'API not responding'
        };
      }

      // Test QBO configuration
      try {
        const configResponse = await this.getCurrentTenantConfig();
        const qboModule = configResponse.modules?.qboAPI;

        // Check for required QBO credentials
        const hasClientId = !!(
          qboModule?.clientId &&
          (typeof qboModule.clientId === 'string' || qboModule.clientId?.ssmPath)
        );

        const hasClientSecret = !!(
          qboModule?.clientSecret &&
          (typeof qboModule.clientSecret === 'string' || qboModule.clientSecret?.ssmPath)
        );

        const hasRealmId = !!(
          qboModule?.realmId &&
          typeof qboModule.realmId === 'string'
        );

        // QBO is configured if we have clientId, clientSecret, and realmId
        const qboConfigured = hasClientId && hasClientSecret && hasRealmId;

        return {
          connected: true,
          apiStatus: 'connected',
          qboConfigured,
        };
      } catch {
        return {
          connected: true,
          apiStatus: 'connected',
          qboConfigured: false,
          error: 'QBO not configured'
        };
      }
    } catch (error) {
      return {
        connected: false,
        apiStatus: 'error',
        qboConfigured: false,
        error: error instanceof Error ? error.message : 'Unknown connection error'
      };
    }
  }

  /**
   * Get QBO trust accounts (accountSubType = TrustAccounts)
   */
  async getQboTrustAccounts(): Promise<Array<{
    accountId: string;
    name: string;
    accountSubType: string;
    accountType: string;
  }>> {
    const response = await api.get<Array<{
      accountId: string;
      name: string;
      accountSubType: string;
      accountType: string;
    }>>(`${this.baseSource}/current/qbo/accounts/trust`);
    return response;
  }

  /**
   * Drop all Redshift tables for a disabled entity
   */
  async dropEntityTables(entityKey: string): Promise<{ success: boolean; dropped: string[]; notFound: string[] }> {
    return api.delete<{ success: boolean; dropped: string[]; notFound: string[] }>(
      `${this.baseSource}/current/dw/entities/${encodeURIComponent(entityKey)}/tables`
    );
  }

  /**
   * Drop the Redshift table for a disabled report kind
   */
  async dropReportTable(reportKind: string): Promise<{ success: boolean; dropped: string[]; notFound: string[] }> {
    return api.delete<{ success: boolean; dropped: string[]; notFound: string[] }>(
      `${this.baseSource}/current/dw/reports/${encodeURIComponent(reportKind)}/table`
    );
  }

  /**
   * Delete rows for a month and re-run the Clio report ETL for that month
   */
  async resyncReportMonth(reportKind: string, monthCode: string): Promise<{ success: boolean; deleted: number; resync: string; period: string }> {
    return api.post<{ success: boolean; deleted: number; resync: string; period: string }>(
      `${this.baseSource}/current/dw/reports/${encodeURIComponent(reportKind)}/resync-month`,
      { monthCode }
    );
  }

  /**
   * Truncate and resync a snapshot-mode report (exportByMonth=false)
   */
  async resyncSnapshotReport(reportKind: string): Promise<{ success: boolean; resync: string; reportKind: string }> {
    return api.post<{ success: boolean; resync: string; reportKind: string }>(
      `${this.baseSource}/current/dw/reports/${encodeURIComponent(reportKind)}/resync-snapshot`
    );
  }

  /**
   * Invoke the DW sync process (same ETL that EventBridge runs on schedule)
   */
  async invokeDwSync(type: 'all' | 'entities' | 'reports' = 'all'): Promise<{ success: boolean; invoked: string[] }> {
    return api.post<{ success: boolean; invoked: string[] }>(
      `${this.baseSource}/current/dw/invoke-sync`,
      { type }
    );
  }

  /**
   * Get QBO trust liability accounts (accountSubType = TrustAccountsLiabilities)
   */
  async getQboTrustLiabilityAccounts(): Promise<Array<{
    accountId: string;
    name: string;
    accountSubType: string;
    accountType: string;
  }>> {
    const response = await api.get<Array<{
      accountId: string;
      name: string;
      accountSubType: string;
      accountType: string;
    }>>(`${this.baseSource}/current/qbo/accounts/liability`);
    return response;
  }
}

export const tenantSettingsApi = new TenantSettingsApi();
